/* eslint-disable max-params, no-param-reassign -- these linting errors come up constantly with reducers */

import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

import type { MedicalReviewState } from "@/constants/Onboarding";
import type { RootState } from "@/store";
import { useAppDispatch } from "@/store/hooks";
import { extraReducersFactory } from "@/store/reducers/reset";

interface OnboardingSliceState {
  currentStepIndex: number;
  isMedicalReviewCleared?: boolean;
  isSplashVisible: boolean;
  medicalReviewState?: MedicalReviewState;
}

const initialState: OnboardingSliceState = {
  currentStepIndex: 0,
  isSplashVisible: true,
};

const onboardingSlice = createSlice({
  extraReducers: extraReducersFactory(initialState),
  initialState,
  name: "onboarding",
  reducers: {
    markSplashAsSeen: (state) => {
      state.isSplashVisible = false;
    },
    reset: () => initialState,
    resetSplash: (state) => {
      state.isSplashVisible = true;
    },
    setCurrentStepIndex: (state, action: PayloadAction<number>) => {
      state.currentStepIndex = action.payload;
    },
    setMedicalReviewCleared: (state) => {
      state.isMedicalReviewCleared = true;
      state.medicalReviewState = undefined;
    },
    setMedicalReviewState: (
      state,
      action: PayloadAction<MedicalReviewState | undefined>,
    ) => {
      state.medicalReviewState = action.payload;
      if (action.payload !== undefined) {
        state.isMedicalReviewCleared = undefined;
      }
    },
  },
});

const {
  markSplashAsSeen: markSplashAsSeenAction,
  reset: resetOnboardingAction,
  resetSplash: resetSplashAction,
  setCurrentStepIndex: setCurrentStepIndexAction,
  setMedicalReviewCleared: setMedicalReviewClearedAction,
  setMedicalReviewState: setMedicalReviewStateAction,
} = onboardingSlice.actions;

const selectOnboardingCurrentStepIndex = (state: RootState) =>
  state.onboarding.currentStepIndex;
const selectOnboardingIsSplashVisible = (state: RootState) =>
  state.onboarding.isSplashVisible;
const selectOnboardingIsMedicalReviewCleared = (state: RootState) =>
  state.onboarding.isMedicalReviewCleared;
const selectOnboardingMedicalReviewState = (state: RootState) =>
  state.onboarding.medicalReviewState;

const useOnboardingDispatch = () => {
  const appDispatch = useAppDispatch();

  return {
    markSplashAsSeen: () => appDispatch(markSplashAsSeenAction()),
    resetOnboarding: () => appDispatch(resetOnboardingAction()),
    resetSplash: () => appDispatch(resetSplashAction()),
    setCurrentStepIndex: (index: number) =>
      appDispatch(setCurrentStepIndexAction(index)),
    setMedicalReviewCleared: () => appDispatch(setMedicalReviewClearedAction()),
    setMedicalReviewState: (reviewState: MedicalReviewState | undefined) =>
      appDispatch(setMedicalReviewStateAction(reviewState)),
  };
};

export default onboardingSlice.reducer;
export {
  markSplashAsSeenAction,
  resetOnboardingAction,
  resetSplashAction,
  selectOnboardingCurrentStepIndex,
  selectOnboardingIsMedicalReviewCleared,
  selectOnboardingIsSplashVisible,
  selectOnboardingMedicalReviewState,
  setCurrentStepIndexAction,
  setMedicalReviewClearedAction,
  setMedicalReviewStateAction,
  useOnboardingDispatch,
};
export type { OnboardingSliceState };
